import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useEffect, useState } from 'react';
import { Button, Checkbox, CircularProgress, ListItemText, MenuItem } from '@mui/material';
import { SelectMenu } from '@livable/saas-react-components';
import { useStore } from 'src/stores';
import { currencyFormatter } from 'src/utils/translator';
import { COLORS, MEASURE } from '../ReimbursementReceiptsCustomTab/palette';
import { Panel, KpiCard, EmptyState } from '../ReimbursementReceiptsCustomTab/components';
import useStyles from '../ReimbursementReceiptsCustomTab/styles';
import { CoverageBar, ServiceVarianceBar, TopPropertiesBar } from './charts';
import { fetchUtilityAllocationExport, EMPTY_REPORT } from './data';
import { nextPropertySelection, ALL_PROPERTIES } from './propertyFilter';
const usd = v => currencyFormatter(Number(v) || 0);
const pct = v => "".concat((Number(v) || 0).toFixed(1), "%");
const IN_BALANCE = 0.005; // |variance| under half a cent = balanced
const varianceColor = v => Math.abs(v) < IN_BALANCE ? MEASURE.paid : v > 0 ? MEASURE.unpaid : COLORS.gold;

// A utility can have an allocation table before its bill is uploaded. That is a missing bill, not $0.00.
const NO_BILL = '—';
const money = v => v === null || v === undefined ? NO_BILL : Number(v).toFixed(2);
const renderVariance = (classes, v) => v === null || v === undefined ? /*#__PURE__*/React.createElement("td", {
  className: classes.num,
  style: {
    color: COLORS.textFaint
  }
}, NO_BILL) : /*#__PURE__*/React.createElement("td", {
  className: classes.num,
  style: {
    color: varianceColor(v),
    fontWeight: 600
  }
}, Number(v).toFixed(2));

// Property Summary — shaded row = no invoices this period (upload gap), red owner_billable > 0,
// green = full recovery (Metabase card #344).
function renderPropertySummary(classes, rows) {
  if (!rows.length) return /*#__PURE__*/React.createElement(EmptyState, {
    message: "No property summary for this period."
  });
  const rowBg = r => r.invoices_in_period === 0 ? 'rgba(235,153,110,0.10)' : undefined;
  return /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.tableWrap, " ").concat(classes.tableScroll)
  }, /*#__PURE__*/React.createElement("table", {
    className: classes.table
  }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Property address"), /*#__PURE__*/React.createElement("th", {
    className: classes.num
  }, "Utilities configured"), /*#__PURE__*/React.createElement("th", {
    className: classes.num
  }, "Billed this period"), /*#__PURE__*/React.createElement("th", {
    className: classes.num
  }, "Invoices"), /*#__PURE__*/React.createElement("th", {
    className: classes.num
  }, "Total invoiced ($)"), /*#__PURE__*/React.createElement("th", {
    className: classes.num
  }, "Total allocated ($)"), /*#__PURE__*/React.createElement("th", {
    className: classes.num
  }, "Owner-billable ($)"))), /*#__PURE__*/React.createElement("tbody", null, rows.map((r, i) => /*#__PURE__*/React.createElement("tr", {
    key: "".concat(r.property_address, "-").concat(i),
    style: {
      background: rowBg(r)
    }
  }, /*#__PURE__*/React.createElement("td", null, r.property_address), /*#__PURE__*/React.createElement("td", {
    className: classes.num
  }, r.utilities_configured), /*#__PURE__*/React.createElement("td", {
    className: classes.num
  }, r.utilities_billed_this_period), /*#__PURE__*/React.createElement("td", {
    className: classes.num
  }, r.invoices_in_period), /*#__PURE__*/React.createElement("td", {
    className: classes.num
  }, Number(r.total_invoiced).toFixed(2)), /*#__PURE__*/React.createElement("td", {
    className: classes.num
  }, Number(r.total_allocated).toFixed(2)), /*#__PURE__*/React.createElement("td", {
    className: classes.num,
    style: {
      color: varianceColor(r.owner_billable),
      fontWeight: 600
    }
  }, Number(r.owner_billable).toFixed(2)))))));
}
function renderInvoiceSummary(classes, rows) {
  if (!rows.length) return /*#__PURE__*/React.createElement(EmptyState, {
    message: "No invoices for this period."
  });
  return /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.tableWrap, " ").concat(classes.tableScroll)
  }, /*#__PURE__*/React.createElement("table", {
    className: classes.table
  }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Property address"), /*#__PURE__*/React.createElement("th", null, "Service"), /*#__PURE__*/React.createElement("th", {
    className: classes.num
  }, "Total bill ($)"), /*#__PURE__*/React.createElement("th", {
    className: classes.num
  }, "Total allocated ($)"), /*#__PURE__*/React.createElement("th", {
    className: classes.num
  }, "Variance ($)"), /*#__PURE__*/React.createElement("th", null, "Bill period"), /*#__PURE__*/React.createElement("th", {
    className: classes.num
  }, "Residents"))), /*#__PURE__*/React.createElement("tbody", null, rows.map((r, i) => /*#__PURE__*/React.createElement("tr", {
    key: "".concat(r.property_address, "-").concat(r.service, "-").concat(i)
  }, /*#__PURE__*/React.createElement("td", null, r.property_address), /*#__PURE__*/React.createElement("td", null, r.service || /*#__PURE__*/React.createElement("span", {
    style: {
      color: COLORS.textFaint
    }
  }, "Unmapped utility")), /*#__PURE__*/React.createElement("td", {
    className: classes.num
  }, money(r.total_bill_amount)), /*#__PURE__*/React.createElement("td", {
    className: classes.num
  }, r.total_allocated.toFixed(2)), renderVariance(classes, r.variance), /*#__PURE__*/React.createElement("td", {
    style: {
      color: COLORS.textMuted
    }
  }, r.bill_period), /*#__PURE__*/React.createElement("td", {
    className: classes.num
  }, r.active_residents))))));
}

// Nested export — each invoice is a header row (shaded) followed by its tenant charge rows
// (indented, muted). Mirrors Metabase card #333 (PRD §6.2).
function renderNested(classes, groups) {
  if (!groups.length) return /*#__PURE__*/React.createElement(EmptyState, {
    message: "No invoices for this period."
  });
  return /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.tableWrap, " ").concat(classes.tableScroll)
  }, /*#__PURE__*/React.createElement("table", {
    className: classes.table
  }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Property / Tenant"), /*#__PURE__*/React.createElement("th", null, "Service / Unit"), /*#__PURE__*/React.createElement("th", {
    className: classes.num
  }, "Total bill ($)"), /*#__PURE__*/React.createElement("th", {
    className: classes.num
  }, "Allocated ($)"), /*#__PURE__*/React.createElement("th", {
    className: classes.num
  }, "Variance / Charge ($)"), /*#__PURE__*/React.createElement("th", null, "Bill period"))), /*#__PURE__*/React.createElement("tbody", null, groups.map((g, gi) => /*#__PURE__*/React.createElement(React.Fragment, {
    key: "".concat(g.property_address, "-").concat(g.service, "-").concat(gi)
  }, /*#__PURE__*/React.createElement("tr", {
    style: {
      background: 'rgba(0,146,153,0.06)'
    }
  }, /*#__PURE__*/React.createElement("td", {
    style: {
      fontWeight: 700
    }
  }, g.property_address), /*#__PURE__*/React.createElement("td", {
    style: {
      fontWeight: 600
    }
  }, g.service), /*#__PURE__*/React.createElement("td", {
    className: classes.num,
    style: {
      fontWeight: 600
    }
  }, money(g.total_bill_amount)), /*#__PURE__*/React.createElement("td", {
    className: classes.num,
    style: {
      fontWeight: 600
    }
  }, Number(g.total_allocated).toFixed(2)), renderVariance(classes, g.variance), /*#__PURE__*/React.createElement("td", {
    style: {
      color: COLORS.textMuted
    }
  }, g.bill_period)), (g.tenants || []).map((t, ti) => /*#__PURE__*/React.createElement("tr", {
    key: "".concat(gi, "-t-").concat(ti)
  }, /*#__PURE__*/React.createElement("td", {
    style: {
      paddingLeft: 34,
      color: COLORS.textMuted
    }
  }, t.name), /*#__PURE__*/React.createElement("td", {
    style: {
      color: COLORS.textMuted
    }
  }, t.unit), /*#__PURE__*/React.createElement("td", {
    className: classes.num
  }), /*#__PURE__*/React.createElement("td", {
    className: classes.num
  }), /*#__PURE__*/React.createElement("td", {
    className: classes.num,
    style: {
      color: COLORS.text
    }
  }, Number(t.charge).toFixed(2)), /*#__PURE__*/React.createElement("td", null))))))));
}

// "Utility Allocation Export" — native (non-embedded) rebuild of Metabase dashboard #101.
// KPIs + coverage + breakdowns + property summary + flat invoice list + nested invoice/tenant
// detail. Data is live from the app's allocations API (same DB Metabase reads).
export const UtilityAllocationExportTab = () => {
  const classes = useStyles();
  const store = useStore();
  const _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    report = _useState2[0],
    setReport = _useState2[1]; // null = loading
  const _useState3 = useState(null),
    _useState4 = _slicedToArray(_useState3, 2),
    selectedCycleId = _useState4[0],
    setSelectedCycleId = _useState4[1];
  // [] = every property. Held separately from the option list so filtering never shrinks the options.
  const _useState5 = useState([]),
    _useState6 = _slicedToArray(_useState5, 2),
    selectedPropertyIds = _useState6[0],
    setSelectedPropertyIds = _useState6[1];
  const _useState7 = useState([]),
    _useState8 = _slicedToArray(_useState7, 2),
    propertyOptions = _useState8[0],
    setPropertyOptions = _useState8[1];
  const _useState9 = useState(false),
    _useState0 = _slicedToArray(_useState9, 2),
    isExporting = _useState0[0],
    setIsExporting = _useState0[1];
  useEffect(() => {
    let alive = true;
    setReport(null);
    fetchUtilityAllocationExport(store, selectedCycleId, selectedPropertyIds).then(r => {
      var _r$propertySummary;
      if (!alive) return;
      setReport(r);
      // Adopt whichever cycle the data layer picked, so the select reflects what is on screen.
      if (!selectedCycleId && r.cycleId) setSelectedCycleId(r.cycleId);
      // The unfiltered response lists every property the caller may see, so it is the option
      // list. Captured once - a filtered response only contains the selection.
      if (!selectedPropertyIds.length && (_r$propertySummary = r.propertySummary) !== null && _r$propertySummary !== void 0 && _r$propertySummary.length) {
        setPropertyOptions(r.propertySummary.map(p => ({
          id: p.property_id,
          address: p.property_address
        })).filter(p => p.id));
      }
    }).catch(() => alive && setReport(EMPTY_REPORT));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCycleId, selectedPropertyIds]);
  const handlePropertyChange = values => setSelectedPropertyIds(nextPropertySelection(values));
  const handleExport = async () => {
    setIsExporting(true);
    try {
      var _resp$headers$fileNa, _resp$headers;
      // Through the store rather than the api layer: createAsync toasts the failure and tracks it as
      // an amplitude api-error. A silent download failure is untraceable. (The report itself still
      // loads through the api layer - those store actions overwrite shared allocation state.)
      const resp = await store.report.downloadUtilityAllocationExportAsync.run({
        allocationCycleId: selectedCycleId,
        monthsBack: 12,
        propertyIds: selectedPropertyIds
      });
      if (!(resp !== null && resp !== void 0 && resp.data)) return;
      const fileName = (_resp$headers$fileNa = (_resp$headers = resp.headers) === null || _resp$headers === void 0 ? void 0 : _resp$headers['file-name']) !== null && _resp$headers$fileNa !== void 0 ? _resp$headers$fileNa : 'utility-allocation-export.csv';
      const url = window.URL.createObjectURL(new Blob([resp.data], {
        type: 'application/octet-stream'
      }));
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } finally {
      setIsExporting(false);
    }
  };
  if (report === null) {
    return /*#__PURE__*/React.createElement("div", {
      className: classes.root
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        padding: 64,
        textAlign: 'center'
      }
    }, /*#__PURE__*/React.createElement(CircularProgress, {
      size: 26,
      style: {
        color: COLORS.teal
      }
    })));
  }
  const kpis = report.kpis,
    coverage = report.coverage,
    byService = report.byService,
    topProperties = report.topProperties,
    propertySummary = report.propertySummary,
    invoices = report.invoices,
    cycles = report.cycles,
    hasTenantDetail = report.hasTenantDetail;
  const hasData = invoices.length > 0;
  return /*#__PURE__*/React.createElement("div", {
    className: classes.root
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.header
  }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("h2", {
    className: classes.title
  }, "Utility Allocation Export"), /*#__PURE__*/React.createElement("p", {
    className: classes.subtitle
  }, "Monthly per-invoice variance and tenant detail: utility bills vs amounts recovered from residents.", report.cycleLabel ? " Billing period: ".concat(report.cycleLabel) : '', report.cycleLabel && !report.cycleIsClosed ? ' (cycle still open — bills may be missing).' : '.')), /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'flex',
      gap: 10,
      alignItems: 'center',
      flexWrap: 'wrap',
      justifyContent: 'flex-end'
    }
  }, propertyOptions.length > 1 && /*#__PURE__*/React.createElement(SelectMenu, {
    id: "uae-property",
    label: "Property",
    multiple: true,
    value: selectedPropertyIds,
    renderValue: () => {
      var _propertyOptions$find, _propertyOptions$find2;
      return selectedPropertyIds.length === 0 ? 'All properties' : selectedPropertyIds.length === 1 ? (_propertyOptions$find = (_propertyOptions$find2 = propertyOptions.find(p => p.id === selectedPropertyIds[0])) === null || _propertyOptions$find2 === void 0 ? void 0 : _propertyOptions$find2.address) !== null && _propertyOptions$find !== void 0 ? _propertyOptions$find : '1 property' : "".concat(selectedPropertyIds.length, " properties");
    },
    onChange: e => handlePropertyChange(e.target.value),
    style: {
      minWidth: 220
    }
  }, /*#__PURE__*/React.createElement(MenuItem, {
    value: ALL_PROPERTIES,
    variant: "multiple"
  }, /*#__PURE__*/React.createElement(Checkbox, {
    checked: selectedPropertyIds.length === 0
  }), /*#__PURE__*/React.createElement(ListItemText, null, "All properties")), propertyOptions.map(p => /*#__PURE__*/React.createElement(MenuItem, {
    key: p.id,
    value: p.id,
    variant: "multiple"
  }, /*#__PURE__*/React.createElement(Checkbox, {
    checked: selectedPropertyIds.includes(p.id)
  }), /*#__PURE__*/React.createElement(ListItemText, null, p.address)))), cycles.length > 0 && /*#__PURE__*/React.createElement(SelectMenu, {
    id: "uae-cycle",
    label: "Billing period",
    value: selectedCycleId !== null && selectedCycleId !== void 0 ? selectedCycleId : '',
    onChange: e => setSelectedCycleId(e.target.value),
    style: {
      minWidth: 200
    }
  }, cycles.map(c => /*#__PURE__*/React.createElement(MenuItem, {
    key: c.id,
    value: c.id
  }, c.label))), /*#__PURE__*/React.createElement(Button, {
    variant: "outlined",
    disabled: !hasData || isExporting,
    onClick: handleExport,
    style: {
      textTransform: 'none',
      borderColor: COLORS.border,
      color: COLORS.teal,
      flexShrink: 0,
      whiteSpace: 'nowrap'
    }
  }, "Export to CSV"))), !hasData ? /*#__PURE__*/React.createElement(EmptyState, {
    message: "No allocation data for this billing period."
  }) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: classes.kpiRow
  }, /*#__PURE__*/React.createElement(KpiCard, {
    label: "Total Invoice $",
    value: usd(kpis.totalInvoice)
  }), /*#__PURE__*/React.createElement(KpiCard, {
    label: "Total Allocated $",
    value: usd(kpis.totalAllocated),
    tone: COLORS.green
  }), /*#__PURE__*/React.createElement(KpiCard, {
    label: "Owner-Billable $",
    value: usd(kpis.ownerBillable),
    tone: kpis.ownerBillable > 0 ? MEASURE.unpaid : COLORS.green,
    footnote: "Not recovered from residents"
  }), /*#__PURE__*/React.createElement(KpiCard, {
    label: "Recovery %",
    value: pct(kpis.recoveryPct * 100),
    tone: kpis.recoveryPct >= 0.999 ? COLORS.green : COLORS.gold
  }), /*#__PURE__*/React.createElement(KpiCard, {
    label: "# Invoices",
    value: kpis.invoiceCount
  })), /*#__PURE__*/React.createElement("div", {
    className: classes.grid
  }, /*#__PURE__*/React.createElement(Panel, {
    title: "Coverage Health \u2014 Last 12 Months",
    subtitle: "Invoice volume per cycle",
    span: 12,
    height: 240
  }, coverage.length ? /*#__PURE__*/React.createElement(CoverageBar, {
    data: coverage
  }) : /*#__PURE__*/React.createElement(EmptyState, {
    message: "No coverage history."
  })), /*#__PURE__*/React.createElement(Panel, {
    title: "Owner-Billable by Service",
    subtitle: "Unrecovered variance by utility",
    span: 6,
    accent: MEASURE.unpaid,
    height: 280
  }, byService.length ? /*#__PURE__*/React.createElement(ServiceVarianceBar, {
    data: byService
  }) : /*#__PURE__*/React.createElement(EmptyState, {
    message: "Nothing owner-billable."
  })), /*#__PURE__*/React.createElement(Panel, {
    title: "Top Properties by Owner-Billable Variance",
    subtitle: "Highest unrecovered cost",
    span: 6,
    accent: MEASURE.unpaid,
    height: 280
  }, topProperties.length ? /*#__PURE__*/React.createElement(TopPropertiesBar, {
    data: topProperties
  }) : /*#__PURE__*/React.createElement(EmptyState, {
    message: "Nothing owner-billable."
  })), /*#__PURE__*/React.createElement(Panel, {
    title: "Property Summary",
    subtitle: "Per active property \u2014 utilities configured vs billed, invoiced vs allocated",
    span: 12
  }, renderPropertySummary(classes, propertySummary)), /*#__PURE__*/React.createElement(Panel, {
    title: "Invoice Summary",
    subtitle: "Flat per-invoice view \u2014 billed vs allocated vs variance",
    span: 12
  }, renderInvoiceSummary(classes, invoices)), hasTenantDetail && /*#__PURE__*/React.createElement(Panel, {
    title: "Utility Allocation Export",
    subtitle: "Invoice header rows with tenant charge detail beneath",
    span: 12
  }, renderNested(classes, invoices)))));
};